import { z } from 'zod';

/** UUID v4-ish identifier used for every entity. */
export const Id = z.string().uuid();
/** Calendar date in IST, stored as YYYY-MM-DD (Asia/Kolkata). */
export const IsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD (IST calendar date)')
  .refine((s) => {
    const [y, m, d] = s.split('-').map(Number) as [number, number, number];
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
  }, 'not a real calendar date');
/** Instant with explicit offset (server emits +05:30 or Z). */
export const Timestamp = z.string().datetime({ offset: true });
/** Law code such as L101. */
export const LawCode = z.string().regex(/^L\d{3}$/, 'law code like L101');
/** E.164 phone number. */
export const E164 = z.string().regex(/^\+[1-9]\d{6,14}$/, 'phone must be E.164, e.g. +919812345678');
/** Money in whole INR (integer rupees; never computed by a model). */
export const Inr = z.number().int().nonnegative();
/** Vertical key, e.g. pharma, education, seo. */
export const VerticalKey = z.string().regex(/^[a-z][a-z0-9_-]{1,31}$/);

export const ErrorCode = z.enum([
  'BAD_REQUEST',
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'VERSION_CONFLICT',
  'LAW_BLOCKED',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'INTERNAL',
  /** 409: a safety lock (owner decision 2026-09-21: never block an admin). The message says what will
   * happen in plain words; resend the same request with `override: { reason }` to change it anyway. */
  'WARNING',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

/** "Change anyway": sent in the body of a request that got a 409 WARNING. The reason is written to history. */
export const OverrideRequest = z.object({ reason: z.string().trim().min(3, 'Write a short reason (3 letters or more)').max(500) }).strict();
export type OverrideRequest = z.infer<typeof OverrideRequest>;

/** Every non-2xx response body. */
export const ErrorEnvelope = z
  .object({
    code: ErrorCode,
    message: z.string().min(1),
    law: LawCode.optional(),
    details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    requestId: z.string().optional(),
  })
  .strict();
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

export const PageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(200).optional(),
});
export type PageQuery = z.infer<typeof PageQuery>;

export const page = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });

export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;

/** Query-string boolean: accepts "true"/"false" only. */
export const QueryBool = z.enum(['true', 'false']).transform((v) => v === 'true');
